/**
 * Pure query-string builder for `GET /api/admin/subscriptions`.
 *
 * Empty / default values are omitted so the URL stays minimal and the
 * React Query cache key lines up with the network call. Returns an
 * empty string (no leading `?`) when nothing is set, so callers can do
 * `${path}${buildSubscriptionsQuery(filters)}` safely.
 *
 * Side-effect free — unit-tested in `subscription-filters.test.ts`.
 */
import type { SubscriptionStatus, SubscriptionTier } from "./constants";

export interface SubscriptionsListFilters {
  readonly query: string;
  readonly status: SubscriptionStatus | null;
  readonly tier: SubscriptionTier | null;
  /** 1-indexed. */
  readonly page: number;
}

export function buildSubscriptionsQuery(filters: SubscriptionsListFilters): string {
  const params = new URLSearchParams();

  const trimmed = filters.query.trim();
  if (trimmed.length > 0) params.set("query", trimmed);
  if (filters.status) params.set("status", filters.status);
  // Normalize tier here too (single point of input normalization), so a raw
  // caller can't leak a padded `?tier=%20TIER_1%20` to the API.
  const trimmedTier = filters.tier?.trim();
  if (trimmedTier) params.set("tier", trimmedTier);
  if (filters.page > 1) params.set("page", String(filters.page));

  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : "";
}
